// tslint:disable: no-console

export enum StorageKey {
  NULL_KEY = "NULL_KEY",
  KAGGLE_THEME = "KAGGLE_THEME",
  KAGGLE_EDITOR = "KAGGLE_EDITOR",
  KAGGLE_LANGUAGE_ID = "KAGGLE_LANGUAGE_ID",
  KAGGLE_LOCAL_STATE = "KAGGLE_LOCAL_STATE",
  KAGGLE_TEST_KEY = "KAGGLE_TEST_KEY",
  KAGGLE_CORGI_MODE = "KAGGLE_CORGI_MODE",
  NUDGE_KEY_GPU_50PCT = "KAGGLE_NUDGE_KEY_GPU_50PCT",
  NUDGE_KEY_GPU_90PCT = "KAGGLE_NUDGE_KEY_GPU_90PCT",
  NUDGE_KEY_RAM_90PCT = "KAGGLE_NUDGE_KEY_RAM_90PCT",
  NUDGE_KEY_DISK_90PCT = "KAGGLE_NUDGE_KEY_DISK_90PCT",
  NUDGE_KEY_IPYNB_DOWNLOAD = "KAGGLE_NUDGE_KEY_IPYNB_DOWNLOAD",
  NUDGE_KEY_COMMONLY_EXPORTED_NOTEBOOK = "KAGGLE_NUDGE_KEY_COMMONLY_EXPORTED_NOTEBOOK",
  NUDGE_KEY_EMPLOYER = "KAGGLE_NUDGE_KEY_EMPLOYER" /** @deprecated */,
  NUDGE_KEY_COMMUNITY_ALL = "KAGGLE_NUDGE_KEY_COMMUNITY_ALL",
  NUDGE_KEY_COMMUNITY_ALL_SEEN = "KAGGLE_NUDGE_KEY_COMMUNITY_ALL_SEEN",
  NOTIFICATIONS = "NOTIFICATIONS",
  RECENTLY_EDITED_ITEMS = "KAGGLE_RECENTLY_EDITED_ITEMS",
  RECENTLY_VIEWED_ITEMS = "KAGGLE_RECENTLY_VIEWED_ITEMS",
  RECENT_BOOKMARKS = "KAGGLE_RECENT_BOOKMARKS",
  EMERGENCY_ALERT_BANNERS_DISMISSED = "KAGGLE_EMERGENCY_ALERT_BANNERS",
  NAV_USER_MENUS_OPEN = "KAGGLE_NAV_USER_MENUS_OPEN",
}

/** A registered callback to be notified when local state changes in another tab. */
type StorageCallback = (key: StorageKey, value: string | null) => void;
export type UnsubscribeStorage = () => void;

const JUPYTER_ORIGINS = [
  "http://localhost:5000",
  "https://staging.kaggleusercontent.com",
  "https://www.kaggleusercontent.com",
];

// TODO(cochamberlain): Jupyter frame is attempting to call some of these on Redux initialization.
// Adding this as temporary fix to stop it from throwing errors and return uninitialized states.
// Longer term fix will be provided as follow up PR that stops Jupyter from trying to interact with storageClient.
const IS_JUPYTER =
  !window.location.origin || JUPYTER_ORIGINS.includes(window.location.origin);

export const isLocalStorageAvailable = () => {
  let success;
  try {
    const storage = window.localStorage;
    success = !!storage;
  } catch (e) {
    success = false;
  }
  return success;
};

export const isSessionStorageAvailable = () => {
  let success;
  try {
    const storage = window.sessionStorage;
    success = !!storage;
  } catch (e) {
    success = false;
  }
  return success;
};

export abstract class StorageClient {
  protected storage: Storage | null;

  constructor() {
    this.storage = null;
  }

  delete(key: StorageKey): void {
    this.storage?.removeItem(key);
  }

  /**
   * read storage setting. If local storage is not available or this is
   * read inside jupyter frame, return null.
   */
  read(key: StorageKey): string | null {
    return this.storage?.getItem(key) ?? null;
  }

  write(key: StorageKey, value: string): void {
    this.storage?.setItem(key, value);
  }
}

// tslint:disable-next-line: max-classes-per-file
export class SessionStorageClient extends StorageClient {
  constructor() {
    super();
    if (!IS_JUPYTER && isSessionStorageAvailable()) {
      this.storage = window.sessionStorage;
    }
  }
}

/**
 * Event listeners only work on `localStorage`, so the `subscribe` method
 * is only defined for this class.
 */
// tslint:disable-next-line: max-classes-per-file
export class LocalStorageClient extends StorageClient {
  private storageCallbacks: Map<StorageKey, Set<StorageCallback>>;

  constructor() {
    super();
    if (!IS_JUPYTER && isLocalStorageAvailable()) {
      this.storage = window.localStorage;
    }
    this.storageCallbacks = new Map<StorageKey, Set<StorageCallback>>();
  }

  /** Handler that gets called when localStorage changes occur in other tabs. */
  private storageEventHandler = (e: StorageEvent) => {
    const key = e.key as StorageKey;
    if (key) {
      this.storageCallbacks.get(key)?.forEach(callback => {
        callback(key, e.newValue);
      });
    }
  };

  subscribe(key: StorageKey, callback: StorageCallback): UnsubscribeStorage {
    if (this.storage === null) {
      return () => {
        console.warn(`key ${key} was never subscribed to in Jupyter frame.`);
      };
    }
    // If this is the first being added, set up the storage change listener.
    // This will notify only when other tabs update this state.
    if (this.storageCallbacks.size === 0) {
      window.addEventListener("storage", this.storageEventHandler);
    }
    if (!this.storageCallbacks.has(key)) {
      this.storageCallbacks.set(key, new Set<StorageCallback>());
    }
    this.storageCallbacks.get(key)!.add(callback);

    // Return unsubscriber.
    return () => {
      const cbSet = this.storageCallbacks.get(key);

      if (cbSet) {
        cbSet.delete(callback);
        if (cbSet.size === 0) {
          this.storageCallbacks.delete(key);
          if (this.storageCallbacks.size === 0) {
            window.removeEventListener("storage", this.storageEventHandler);
          }
        }
      }
    };
  }
}

export const storageClient = new LocalStorageClient();
